const MagicLinkManager = (() => {
  const CONFIG = {
    urlParam: 'vip_user',
    cookieName: 'tss_magic_link',
    cookieDurationDays: 7,
    cookieMaxAgeMs: 24 * 60 * 60 * 1000, // 24 hours
    sectionSelector: 'cart-drawer-offer-progress',
    bodyClass: 'has-magic-link',
    allowedValues: null, // Add allowed values (e.g. ['vip']) to lock to specific codes,
    alwaysOn: true
  };

  const DAY_IN_MS = 24 * 60 * 60 * 1000;

  const getCookie = (name) => {
    const cookies = document.cookie ? document.cookie.split('; ') : [];
    for (let i = 0; i < cookies.length; i += 1) {
      const parts = cookies[i].split('=');
      const cookieName = parts.shift();
      const cookieValue = parts.join('=');
      if (cookieName === name) {
        try {
          return decodeURIComponent(cookieValue);
        } catch (error) {
          return cookieValue;
        }
      }
    }
    return '';
  };

  const setCookie = (name, value, days) => {
    const expires = new Date(Date.now() + days * DAY_IN_MS).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  };

  const parseStoredCookie = (value = '') => {
    if (!value) return { code: '', createdAt: null };
    const parts = value.split('|');
    const code = parts[0] || '';
    const createdAt = Number(parts[1]);
    return { code, createdAt: Number.isFinite(createdAt) ? createdAt : null };
  };

  const isExpired = (timestamp) =>
    Number.isFinite(timestamp) && Date.now() - timestamp > (CONFIG.cookieMaxAgeMs || DAY_IN_MS);

  const isAllowedValue = (value) => {
    if (!CONFIG.allowedValues || !Array.isArray(CONFIG.allowedValues) || CONFIG.allowedValues.length === 0) {
      return true;
    }
    return CONFIG.allowedValues.includes(value);
  };

  const captureFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has(CONFIG.urlParam)) {
      return false;
    }
    const value = params.get(CONFIG.urlParam) || '1';
    if (!isAllowedValue(value)) {
      return false;
    }
    setCookie(CONFIG.cookieName, `${value}|${Date.now()}`, CONFIG.cookieDurationDays);
    return true;
  };

  const getStoredCode = () => {
    const { code, createdAt } = parseStoredCookie(getCookie(CONFIG.cookieName));
    if (isExpired(createdAt)) {
      setCookie(CONFIG.cookieName, '', -1);
      return '';
    }
    return code;
  };

  const isActive = () => CONFIG.alwaysOn || Boolean(getStoredCode());

  const applyBodyClass = (active) => {
    if (!document.body) return;
    document.body.classList.toggle(CONFIG.bodyClass, active);
  };

  const applyVisibility = (root = document) => {
    const shouldShow = isActive();
    const sections = root.querySelectorAll(CONFIG.sectionSelector);
    sections.forEach((section) => {
      if (shouldShow) {
        section.style.removeProperty('display');
        section.dataset.magicLinkActive = 'true';
      } else {
        section.style.display = 'none';
        section.dataset.magicLinkActive = 'false';
      }
    });
    applyBodyClass(shouldShow);
  };

  captureFromUrl();

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => applyVisibility());
  } else {
    applyVisibility();
  }

  return {
    isActive,
    applyVisibility,
    getCode: getStoredCode
  };
})();

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.itemCount = this.querySelector('[data-ref="itemCount"]');
    this.background = this.querySelector('[data-ref="background"]');
    this.cartOfferprogress = null;
    this.magicLinkActive = MagicLinkManager.isActive();

    // keep a handle so we can clean up later
    this.cartUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.cartUpdate,
      ({ source }) => {
        // ignore updates that aren’t relevant if you want
        if (source !== 'cart-items') return;
        //this.refreshMagicLinkState();
      }
    );
  }

  connectedCallback() {
    this.refreshMagicLinkState();

    document.addEventListener('cartOpen', () => {
      this.open();
      if (this.magicLinkActive && this.cartOfferprogress) {
        this.cartOfferprogress.onCartOpen();
      }
    });

    document.addEventListener('cartChanged', () => {
      if (this.magicLinkActive && this.cartOfferprogress) {
        this.cartOfferprogress.onCartOpen();
      }
    });

    this.querySelector('.cart-drawer__background').addEventListener('click', this.close.bind(this));

    document.querySelectorAll('[data-ref="closeSideCart"]').forEach((button) =>
      button.addEventListener('click', this.close.bind(this))
    );
  }

  open() {
    this.classList.add('is-active');
    document.body.classList.add('drawer-open');
  }

  close() {
    this.classList.remove('is-active');
    document.body.classList.remove('drawer-open');
  }

  renderContents(parsedState) {
    this.querySelector('.cart-main').classList.contains('cart-main--empty') &&
    this.querySelector('.cart-main').classList.remove('cart-main--empty');

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.getIconsToRender().forEach((section) => {
      const sectionElementsToReplace = document.querySelectorAll(section.selector);

      sectionElementsToReplace.forEach((sectionElementToReplace) => {
        sectionElementToReplace.style.display = 'flex';
        const newSection = new DOMParser().parseFromString(parsedState.sections[section.id], 'text/html').querySelector('.shopify-section');
        if (newSection) {
          sectionElementToReplace.innerHTML = newSection.innerHTML;
        }
      })
    });

    document.querySelector('.cart-drawer__background').addEventListener('click', this.close.bind(this));

    document.querySelectorAll('[data-ref="closeSideCart"]').forEach((button) =>
      button.addEventListener('click', this.close.bind(this))
    );

    this.refreshMagicLinkState();
    this.open();
    document.dispatchEvent(new Event('cartUpdate'));

  }

  getSectionInnerHTML(html, selector = '.js-contents') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '.cart-main',
      }
    ];
  }

  getIconsToRender() {
    return [
      {
        id: 'cart-icon-bubble',
        selector: '.cart-icon-bubble',
      }
    ];
  }

  getSectionDOM(html, selector = '.js-contents') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

  refreshMagicLinkState() {
    this.magicLinkActive = MagicLinkManager.isActive();
    MagicLinkManager.applyVisibility(this);
    this.cartOfferprogress = this.magicLinkActive ? this.querySelector('cart-drawer-offer-progress') : null;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      }
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

class CartDrawerOfferProgress extends CartItems {
  constructor() {
    super();
    this.magicLinkActive = MagicLinkManager.isActive();
    this.progressBarFill = this.querySelector('[data-ref="progressBarFill"]');
    this.tierImages = this.querySelectorAll('[data-offer-tier]');
    this.barElement = this.querySelector('.cart-drawer-offer-progress__bar');
    if (!this.magicLinkActive || !this.progressBarFill || !this.barElement) {
      return;
    }
    this.cartTotal = Number(this.barElement.dataset.cartTotal);
    this.cartId = this.getCookie('cart');
    this.cartDrawer = document.querySelector('cart-drawer');
    this.offerTierMax = Number(this.barElement.dataset.offerTierMax);
    this.progressBarText = this.querySelector('[data-ref="progressBarText"]');
    this.sectionsToRender = this.cartDrawer
      ? [
          ...this.cartDrawer.getSectionsToRender().map((section) => section.id),
          ...this.cartDrawer.getIconsToRender().map((section) => section.id),
        ]
      : ['cart-drawer', 'cart-icon-bubble'];
    this.freeProducts = [
      {
        id: 55027980894593,
        minimumSpend: 5000,
        maximumSpend: 9999
      },
      {
        id: 54944280510849,
        minimumSpend: 10000,
        maximumSpend: 14999
      },
      {
        id: 54849456439681,
        minimumSpend: 15000,
        maximumSpend: 9999999
      }
    ];
    this.freeProductIds = new Set(this.freeProducts.map((product) => product.id));
    this.isProcessingCart = false;
    this.shouldRerun = false;
    this.scheduleCartSync();

  }

  connectedCallback() {
    if (!this.magicLinkActive || !this.progressBarFill) {
      return;
    }

    if (super.connectedCallback) {
      super.connectedCallback();
    }

    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }

    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event = {}) => {
      const source = event.source;
      if (source === 'product-form' || source === 'cart-items') {
        this.scheduleCartSync();
      }
    });
  }

  cartUpdateUnsubscriber = undefined;

  disconnectedCallback() {
    if (super.disconnectedCallback) {
      super.disconnectedCallback();
    }
  }

  getCookie(name) {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  }

  animateProgressBar() {
    if (!this.magicLinkActive || !this.progressBarFill) {
      return;
    }
    this.progressBarFill.style.width = `0%`;
    this.progressBarFill.style.transition = 'width 0.5s ease-in-out';
    const tierMax = this.offerTierMax || 1;
    const currentProgress = Math.min((this.cartTotal / tierMax) * 100, 100);
    this.progressBarFill.style.width = `${Math.max(currentProgress, 0)}%`;
  }

  async onCartDrawerUpdate() {
    if (!this.magicLinkActive || !this.progressBarFill) {
      return;
    }

    if (this.isProcessingCart) {
      this.shouldRerun = true;
      return;
    }

    this.isProcessingCart = true;

    try {
      this.cartData = await this.getCartData();
      const cartItems = Array.isArray(this.cartData?.items) ? this.cartData.items : [];
      const qualifiedSpend = this.calculateQualifiedSpend(cartItems);
      this.cartTotal = qualifiedSpend;

      const targetGift = this.getTargetGift(qualifiedSpend);
      const giftLineItems = cartItems.filter((item) => this.isFreeGiftVariant(item.variant_id));
      const currentGift = giftLineItems[0] || null;
      const extraGiftLineItems = giftLineItems.slice(1);

      const cartMutated = await this.syncFreeGift(targetGift, currentGift, extraGiftLineItems);

      this.updateProgressBar(qualifiedSpend);

      if (cartMutated) {
        this.shouldRerun = true;
      }
    } catch (error) {
      console.error('Error fetching cart data:', error);
    } finally {
      this.isProcessingCart = false;
      if (this.shouldRerun) {
        this.shouldRerun = false;
        this.scheduleCartSync();
      }
    }
  }

  async getCartData() {
    const response = await fetch('/cart.json');
    return await response.json();
  }

  async syncFreeGift(targetGift, currentGift, extraGiftLineItems = []) {
    let cartMutated = false;

    if (extraGiftLineItems.length) {
      for (const extraGift of extraGiftLineItems) {
        await this.removeFreeProduct(extraGift.key);
      }
      cartMutated = true;
    }

    if (!targetGift && currentGift) {
      await this.removeFreeProduct(currentGift.key);
      return true;
    }

    if (targetGift && !currentGift) {
      await this.addFreeProduct(targetGift.id);
      return true;
    }

    if (targetGift && currentGift && currentGift.variant_id !== targetGift.id) {
      await this.removeFreeProduct(currentGift.key);
      await this.addFreeProduct(targetGift.id);
      return true;
    }

    if (targetGift && currentGift && currentGift.quantity !== 1) {
      await this.updateFreeProductQuantity(currentGift.key, 1);
      cartMutated = true;
    }

    return cartMutated;
  }

  async removeFreeProduct(lineKey) {
    return this.applyCartRequest(window.routes.cart_change_url, {
      id: lineKey,
      quantity: 0,
    });
  }

  async addFreeProduct(variantId) {
    return this.applyCartRequest(window.routes.cart_add_url, {
      items: [{
        quantity: 1,
        id: variantId
      }]
    });
  }

  async updateFreeProductQuantity(lineKey, quantity) {
    return this.applyCartRequest(window.routes.cart_change_url, {
      id: lineKey,
      quantity,
    });
  }

  async applyCartRequest(endpoint, payload) {
    const config = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        ...payload,
        sections: this.sectionsToRender,
        sections_url: window.location.pathname,
      }),
    };

    try {
      const response = await fetch(endpoint, config);
      const responseText = await response.text();
      const parsedState = JSON.parse(responseText);

      if (parsedState?.sections && this.cartDrawer) {
        this.cartDrawer.renderContents(parsedState);
      }

      return parsedState;
    } catch (error) {
      console.error('Unable to complete cart request', error);
      return null;
    }
  }

  async onCartOpen() {
    if (!this.magicLinkActive) {
      return;
    }
    this.animateProgressBar();
  }

  updateProgressBar(totalExcludingSewingMachines) {
    if (!this.magicLinkActive) {
      return;
    }
    const tierMax = this.offerTierMax || 1;
    const progress = Math.min((totalExcludingSewingMachines / tierMax) * 100, 100);
    this.progressBarFill.style.width = `${Math.max(progress, 0)}%`;

    // Update the tier images based on the progress
    this.tierImages.forEach((tierImage) => {
      const offerTier = Number(tierImage.dataset.offerTier);
      if (progress >= offerTier) {
        tierImage.classList.add('is-active');
      } else {
        tierImage.classList.remove('is-active');
      }
    });
  }

  calculateQualifiedSpend(items) {
    return items.reduce((total, item) => {
      const productType = (item.product_type || '').toLowerCase().trim();
      const isSewingMachine = productType === 'sewing machines' || productType === 'sewing machine';
      const isFreeGift = this.isFreeGiftVariant(item.variant_id);
      if (isSewingMachine || isFreeGift) {
        return total;
      }
      return total + Number(item.final_line_price || 0);
    }, 0);
  }

  getTargetGift(qualifiedSpend) {
    return (
      this.freeProducts.find(
        (product) => qualifiedSpend >= product.minimumSpend && qualifiedSpend <= product.maximumSpend
      ) || null
    );
  }

  isFreeGiftVariant(variantId) {
    const numericId = Number(variantId);
    return this.freeProductIds.has(numericId);
  }

  scheduleCartSync() {
    if (!this.magicLinkActive) {
      return;
    }
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(() => this.onCartDrawerUpdate());
    } else {
      window.setTimeout(() => this.onCartDrawerUpdate(), 0);
    }
  }

  formatCurrency(valueInCents) {
    const pounds = Math.max(0, valueInCents) / 100;
    const formatted = pounds.toFixed(2);
    return `£${formatted.replace(/\.00$/, '')}`;
  }
}

customElements.define('cart-drawer-offer-progress', CartDrawerOfferProgress);
